import React, { useState, useEffect, useRef, useMemo } from 'react';
import './ViewLogbooks.css';
import collegeConfig from '../../config/collegeConfig';
import ReattemptRequestModal from '../shared/ReattemptRequestModal';
import EntryDetailsModal from '../shared/EntryDetailsModal';
import MonthPicker from '../shared/MonthPicker';
import { getStatusBadge, formatDate, renderReviewList, renderEntryData } from '../shared/logbookDisplayUtils';
import {
  FaBookMedical,
  FaSearch,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaEdit,
  FaEye,
  FaSave,
  FaTimes,
  FaCalendarAlt,
  FaUser,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaSpinner,
  FaCheckDouble,
  FaStar,
  FaPaperclip,
  FaHistory,
  FaUserFriends,
  FaRedo
} from 'react-icons/fa';

const ITEMS_PER_PAGE = 20;

const ViewLogbooks = ({ user, assignedCourse, myStudentIds, allStudents, batchLabels = {} }) => {
  const [logbooks, setLogbooks] = useState([]);
  const [filteredLogbooks, setFilteredLogbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [selectedModule, setSelectedModule] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDecision, setSelectedDecision] = useState('all');
  const [selectedVerifiedBy, setSelectedVerifiedBy] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [modules, setModules] = useState([]);

  const contentRef = useRef(null);

  // Expanded cards state - all expanded by default (track collapsed ones)
  const [expandedCards, setExpandedCards] = useState({});

  // Details modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsEntryId, setDetailsEntryId] = useState(null);

  // Verification modal states
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [selectedLogbook, setSelectedLogbook] = useState(null);
  const [verificationData, setVerificationData] = useState({
    attemptActivity: '',
    rating: '',
    facultyDecision: '',
    remarks: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isReverify, setIsReverify] = useState(false);
  const [savingVerification, setSavingVerification] = useState(false);

  // HOD/Dean reattempt-request state
  const [showReattemptModal, setShowReattemptModal] = useState(false);

  // Bulk verification states
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectedForBulk, setSelectedForBulk] = useState([]);
  const [bulkVerificationData, setBulkVerificationData] = useState({
    attemptActivity: '',
    rating: '',
    facultyDecision: '',
    remarks: ''
  });
  const [savingBulk, setSavingBulk] = useState(false);
  
  // View states
  const [expandedMonths, setExpandedMonths] = useState(() => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return { [currentMonth]: true };
  });

  // Pagination: track current page per month group
  const [monthPages, setMonthPages] = useState({});

  // Lookup map: studentId -> batch, used to show a batch tag next to each student
  const studentBatchMap = useMemo(() => {
    const map = {};
    (allStudents || []).forEach(s => { map[String(s._id || s.id)] = s.batch || ''; });
    return map;
  }, [allStudents]);

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    verified: 0,
    pending: 0,
    needsRevision: 0
  });

  // Browser navigation support
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const detailsId = params.get('details');
    const verifyId = params.get('verify');
    const bulkMode = params.get('bulk');

    if (detailsId) {
      return;
    }
    if (verifyId) {
      return;
    }
    if (bulkMode === 'true') {
      setShowBulkModal(true);
      document.body.style.overflow = 'hidden';
    }
  }, []);

  useEffect(() => {
    const handlePopState = (event) => {
      const params = new URLSearchParams(window.location.search);
      const detailsId = params.get('details');
      const verifyId = params.get('verify');
      const bulkMode = params.get('bulk');

      setShowDetailsModal(false);
      setShowVerificationModal(false);
      setShowBulkModal(false);

      if (detailsId && logbooks.length > 0) {
        const logbook = logbooks.find(l => l._id === detailsId);
        if (logbook) {
          handleViewDetails(logbook);
        }
      } else if (verifyId && logbooks.length > 0) {
        const logbook = logbooks.find(l => l._id === verifyId);
        if (logbook) {
          handleVerificationClick(logbook);
        }
      } else if (bulkMode === 'true') {
        setShowBulkModal(true);
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [logbooks]);

  const updateURL = (modal, id = null) => {
    const params = new URLSearchParams(window.location.search);
    
    params.delete('details');
    params.delete('verify');
    params.delete('bulk');

    if (modal === 'details' && id) {
      params.set('details', id);
    } else if (modal === 'verify' && id) {
      params.set('verify', id);
    } else if (modal === 'bulk') {
      params.set('bulk', 'true');
    }

    const newURL = params.toString() ? 
      `${window.location.pathname}?${params.toString()}` : 
      window.location.pathname;
    
    window.history.pushState({}, '', newURL);
  };

  const closeAllModals = () => {
    setShowDetailsModal(false);
    setShowVerificationModal(false);
    setShowBulkModal(false);
    window.history.pushState({}, '', window.location.pathname);
    document.body.style.overflow = 'unset';
  };

  useEffect(() => {
    if (assignedCourse?._id) {
      fetchLogbooks();
      fetchModules();
    }
  }, [assignedCourse]);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, selectedBatch, selectedModule, selectedStatus, selectedDecision, selectedVerifiedBy, selectedMonth, logbooks, myStudentIds]);

  // Reset to first page for all month groups when filters change
  useEffect(() => {
    setMonthPages({});
  }, [searchTerm, selectedBatch, selectedModule, selectedStatus, selectedDecision, selectedVerifiedBy, selectedMonth, myStudentIds]);

  useEffect(() => {
    const verified = filteredLogbooks.filter(l => l.status === 'approved').length;
    const pending = filteredLogbooks.filter(l => l.status === 'submitted' || l.status === 'under_review').length;
    const needsRevision = filteredLogbooks.filter(l => l.status === 'needs_revision').length;
    
    setStats({
      total: filteredLogbooks.length,
      verified,
      pending,
      needsRevision
    });
  }, [filteredLogbooks]);

  const fetchLogbooks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `${collegeConfig.api.baseURL}/logbook/entries/course/${assignedCourse._id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setLogbooks(data.entries || []);
      } else {
        throw new Error('Failed to fetch logbooks');
      }
    } catch (error) {
      console.error('Error fetching logbooks:', error);
      setError('Failed to load logbook entries');
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `${collegeConfig.api.baseURL}/courses/${assignedCourse._id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setModules(data.course.modules || []);
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...logbooks];

    // Filter by My Students selection
    if (myStudentIds && myStudentIds.length > 0) {
      filtered = filtered.filter(logbook => {
        const studentId = logbook.studentId?._id || logbook.studentId;
        return myStudentIds.includes(studentId);
      });
    }

    // Batch filtering: always restrict to valid batch years, optionally to a specific batch
    if (allStudents?.length > 0) {
      const minBatchYear = new Date().getFullYear() - 4;
      const studentBatchMap = {};
      allStudents.forEach(s => { studentBatchMap[String(s._id || s.id)] = s.batch || ''; });

      filtered = filtered.filter(logbook => {
        const sid = String(logbook.studentId?._id || logbook.studentId);
        const batch = studentBatchMap[sid] || '';
        // Always filter out old batches
        if (batch) {
          const startYear = parseInt(batch.split('-')[0]);
          if (!isNaN(startYear) && startYear < minBatchYear) return false;
        }
        // If a specific batch is selected, also match it
        if (selectedBatch !== 'all') return batch === selectedBatch;
        return true;
      });
    }

    if (searchTerm) {
      filtered = filtered.filter(logbook => {
        const studentName = `${logbook.studentId?.profile?.firstName || ''} ${logbook.studentId?.profile?.lastName || ''}`.toLowerCase();
        const studentEmail = logbook.studentId?.email?.toLowerCase() || '';
        return studentName.includes(searchTerm.toLowerCase()) ||
               studentEmail.includes(searchTerm.toLowerCase());
      });
    }
    
    if (selectedModule !== 'all') {
      filtered = filtered.filter(logbook => logbook.moduleId === selectedModule);
    }
    
    if (selectedStatus !== 'all') {
      if (selectedStatus === 'verified') {
        filtered = filtered.filter(logbook => logbook.status === 'approved');
      } else if (selectedStatus === 'pending') {
        filtered = filtered.filter(logbook => 
          logbook.status === 'submitted' || logbook.status === 'under_review'
        );
      } else if (selectedStatus === 'needs_revision') {
        filtered = filtered.filter(logbook => logbook.status === 'needs_revision');
      }
    }
    
    // Faculty decision filter
    if (selectedDecision !== 'all') {
      if (selectedDecision === 'none') {
        filtered = filtered.filter(logbook => !logbook.review?.facultyDecision);
      } else {
        filtered = filtered.filter(logbook => logbook.review?.facultyDecision === selectedDecision);
      }
    }

    // Verified by filter
    if (selectedVerifiedBy !== 'all') {
      if (selectedVerifiedBy === 'me') {
        const myId = user.id || user._id;
        filtered = filtered.filter(logbook => logbook.review?.reviewerId === myId || logbook.review?.reviewerId?._id === myId);
      } else if (selectedVerifiedBy === 'others') {
        const myId = user.id || user._id;
        filtered = filtered.filter(logbook => logbook.review?.reviewerId && logbook.review?.reviewerId !== myId && logbook.review?.reviewerId?._id !== myId);
      } else if (selectedVerifiedBy === 'unverified') {
        filtered = filtered.filter(logbook => !logbook.review?.reviewerId);
      }
    }

    // Month filter (using local time to avoid UTC timezone bugs)
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    filtered = filtered.filter(logbook => {
      const d = new Date(logbook.createdAt);
      const logbookMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const isVerified = logbook.status === 'approved';
      const isCurrentMonth = logbookMonth === currentMonth;
      const isSelectedMonth = logbookMonth === selectedMonth;
      
      // If current month is selected
      if (selectedMonth === currentMonth) {
        // Show all entries from current month
        if (isCurrentMonth) return true;
        // Show only unverified from previous months
        if (!isVerified) return true;
        return false;
      }
      
      // If a specific past month is selected
      // Show only entries from that specific month (both verified and unverified)
      return isSelectedMonth;
    });
    
    setFilteredLogbooks(filtered);
  };

  const handleViewDetails = (logbook) => {
    setDetailsEntryId(logbook._id);
    setShowDetailsModal(true);
    updateURL('details', logbook._id);
    document.body.style.overflow = 'hidden';
  };

  // Shared reviewer-identity check — used to decide "Edit Verification" vs "Re-verify" vs "Verify"
  // both on the card action button and when the verify modal opens.
  const getReviewerMatch = (logbook) => {
    const myId = user.id || user._id;
    const reviewerId = logbook.review?.reviewerId?._id || logbook.review?.reviewerId;
    const hasReview = logbook.review && reviewerId;
    return { hasReview, isSameReviewer: hasReview && reviewerId === myId };
  };

  const handleVerificationClick = (logbook) => {
    const { hasReview, isSameReviewer } = getReviewerMatch(logbook);

    // Only the original reviewer or a HOD may act on an already-reviewed entry
    if (hasReview && !isSameReviewer && !user.profile?.isHOD) {
      return;
    }

    setSelectedLogbook(logbook);
    updateURL('verify', logbook._id);
    document.body.style.overflow = 'hidden';

    if (hasReview && isSameReviewer) {
      setVerificationData({
        attemptActivity: logbook.review.attemptActivity || '',
        rating: logbook.review.rating || '',
        facultyDecision: logbook.review.facultyDecision || '',
        remarks: logbook.review.feedback || ''
      });
      setIsEditing(true);
      setIsReverify(false);
    } else {
      setVerificationData({
        attemptActivity: '',
        rating: '',
        facultyDecision: '',
        remarks: ''
      });
      setIsEditing(false);
      setIsReverify(!!(hasReview && !isSameReviewer));
    }

    setShowVerificationModal(true);
  };

  const handleVerificationSubmit = async () => {
    try {
      setSavingVerification(true);
      const token = localStorage.getItem('token');
      
      const reviewData = {
        reviewerId: user.id || user._id,
        reviewerName: `${user.profile?.firstName} ${user.profile?.lastName}`,
        feedback: verificationData.remarks
      };
      
      if (verificationData.attemptActivity) {
        reviewData.attemptActivity = verificationData.attemptActivity;
      }
      if (verificationData.rating) {
        reviewData.rating = verificationData.rating;
      }
      if (verificationData.facultyDecision) {
        reviewData.facultyDecision = verificationData.facultyDecision;
        reviewData.status = verificationData.facultyDecision === 'C' ? 'approved' : 'needs_revision';
      } else {
        reviewData.status = 'approved';
      }
      
      const response = await fetch(
        `${collegeConfig.api.baseURL}/logbook/entries/${selectedLogbook._id}/review`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(reviewData)
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setLogbooks(prev => prev.map(lb => lb._id === selectedLogbook._id ? data.entry : lb));
        setSuccess(isEditing ? 'Verification updated successfully' : isReverify ? 'Re-verified successfully' : 'Logbook verified successfully');
        closeAllModals();
      } else {
        throw new Error('Failed to verify logbook');
      }
    } catch (error) {
      console.error('Error verifying logbook:', error);
      setError('Failed to verify logbook');
    } finally {
      setSavingVerification(false);
    }
  };

  const handleReattemptClick = (logbook) => {
    setSelectedLogbook(logbook);
    setShowReattemptModal(true);
  };

  const handleBulkVerify = () => {
    const unverifiedEntries = filteredLogbooks.filter(
      l => l.status !== 'approved' && l.status !== 'draft'
    );
    
    if (unverifiedEntries.length === 0) {
      setError('No unverified entries to verify');
      return;
    }
    
    setShowBulkModal(true);
    updateURL('bulk');
    document.body.style.overflow = 'hidden';
    setSelectedForBulk([]);
    setBulkVerificationData({
      attemptActivity: '',
      rating: '',
      facultyDecision: '',
      remarks: ''
    });
  };

  const toggleBulkSelection = (logbookId) => {
    setSelectedForBulk(prev => {
      if (prev.includes(logbookId)) {
        return prev.filter(id => id !== logbookId);
      } else {
        return [...prev, logbookId];
      }
    });
  };

  const selectAllForBulk = () => {
    const unverifiedEntries = filteredLogbooks.filter(
      l => l.status !== 'approved' && l.status !== 'draft'
    );
    setSelectedForBulk(unverifiedEntries.map(l => l._id));
  };

  const handleBulkVerificationSubmit = async () => {
    if (selectedForBulk.length === 0) {
      setError('Please select at least one entry to verify');
      return;
    }

    try {
      setSavingBulk(true);
      const token = localStorage.getItem('token');
      
      const reviewData = {
        reviewerId: user.id || user._id,
        reviewerName: `${user.profile?.firstName} ${user.profile?.lastName}`,
        feedback: bulkVerificationData.remarks
      };
      
      if (bulkVerificationData.attemptActivity) {
        reviewData.attemptActivity = bulkVerificationData.attemptActivity;
      }
      if (bulkVerificationData.rating) {
        reviewData.rating = bulkVerificationData.rating;
      }
      if (bulkVerificationData.facultyDecision) {
        reviewData.facultyDecision = bulkVerificationData.facultyDecision;
        reviewData.status = bulkVerificationData.facultyDecision === 'C' ? 'approved' : 'needs_revision';
      } else {
        reviewData.status = 'approved';
      }
      
      const promises = selectedForBulk.map(logbookId =>
        fetch(`${collegeConfig.api.baseURL}/logbook/entries/${logbookId}/review`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(reviewData)
        })
      );
      
      const results = await Promise.all(promises);
      const successResponses = results.filter(r => r.ok);
      const successCount = successResponses.length;

      if (successCount > 0) {
        const updatedEntries = await Promise.all(successResponses.map(r => r.json()));
        setLogbooks(prev => prev.map(lb => {
          const updated = updatedEntries.find(d => d.entry?._id === lb._id);
          return updated ? updated.entry : lb;
        }));
        setSuccess(`Successfully verified ${successCount} logbook entries`);
        closeAllModals();
        setSelectedForBulk([]);
      } else {
        throw new Error('Failed to verify entries');
      }
    } catch (error) {
      console.error('Error bulk verifying:', error);
      setError('Failed to verify some entries');
    } finally {
      setSavingBulk(false);
    }
  };

  const toggleMonth = (month) => {
    setExpandedMonths(prev => ({
      ...prev,
      [month]: prev[month] === false ? true : false
    }));
  };

  const toggleCard = (logbookId) => {
    setExpandedCards(prev => ({
      ...prev,
      [logbookId]: prev[logbookId] === false ? true : false
    }));
  };

  const setMonthPage = (monthKey, page) => {
    setMonthPages(prev => ({ ...prev, [monthKey]: page }));
    setTimeout(() => {
      contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const getRatingBadge = (rating) => {
    const ratings = {
      'E': { text: 'Exceeds Expectations', class: 'rating-exceeds', icon: '⭐⭐⭐' },
      'M': { text: 'Meets Expectations', class: 'rating-meets', icon: '⭐⭐' },
      'B': { text: 'Below Expectations', class: 'rating-below', icon: '⭐' }
    };
    return ratings[rating] || null;
  };

  const getModuleName = (moduleId) => {
    const module = modules.find(m => m.moduleId === moduleId);
    return module ? module.name : 'Unknown Module';
  };

  const groupLogbooksByMonth = () => {
    const grouped = {};

    filteredLogbooks.forEach(logbook => {
      const date = new Date(logbook.createdAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          name: monthName,
          entries: []
        };
      }
      
      grouped[monthKey].entries.push(logbook);
    });
    
    return Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0]));
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 1500);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 1500);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading) {
    return (
      <div className="logbooks-loading">
        <FaSpinner className="spinner" />
        <p>Loading logbook entries...</p>
      </div>
    );
  }

  return (
    <div className="view-logbooks-container">
      <div className="logbooks-header">
        <h2>Student Logbooks</h2>
        <p>Review and verify student submissions for {assignedCourse?.name}</p>
      </div>

      {success && <div className="vl-toast vl-toast-success"><FaCheckCircle />{success}</div>}
      {error && <div className="vl-toast vl-toast-error"><FaExclamationTriangle />{error}</div>}

      <div className="logbooks-stats">
        <div className="stat-card-mini total">
          <div className="stat-icon-mini"><FaBookMedical /></div>
          <div className="stat-content-mini">
            <span className="stat-value-mini">{stats.total}</span>
            <span className="stat-label-mini">Total Submissions</span>
          </div>
        </div>
        
        <div className="stat-card-mini verified">
          <div className="stat-icon-mini"><FaCheckCircle /></div>
          <div className="stat-content-mini">
            <span className="stat-value-mini">{stats.verified}</span>
            <span className="stat-label-mini">Verified</span>
          </div>
        </div>
        
        <div className="stat-card-mini pending">
          <div className="stat-icon-mini"><FaClock /></div>
          <div className="stat-content-mini">
            <span className="stat-value-mini">{stats.pending}</span>
            <span className="stat-label-mini">Pending Review</span>
          </div>
        </div>
        
        <div className="stat-card-mini revision">
          <div className="stat-icon-mini"><FaExclamationTriangle /></div>
          <div className="stat-content-mini">
            <span className="stat-value-mini">{stats.needsRevision}</span>
            <span className="stat-label-mini">Needs Revision</span>
          </div>
        </div>
      </div>

      <div className="logbooks-filters">
        <div className="filter-row">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by student name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <div className="filter-item">
              <label>Batch</label>
              <select value={selectedBatch} onChange={(e) => setSelectedBatch(e.target.value)}>
                <option value="all">All Batches</option>
                {(() => {
                  const minStartYear = new Date().getFullYear() - 4;
                  return [...new Set((allStudents || []).map(s => s.batch).filter(Boolean))]
                    .filter(b => { const y = parseInt(b.split('-')[0]); return !isNaN(y) && y >= minStartYear; })
                    .sort()
                    .map(b => <option key={b} value={b}>{b}</option>);
                })()}
              </select>
            </div>

            <div className="filter-item">
              <label>Module</label>
              <select value={selectedModule} onChange={(e) => setSelectedModule(e.target.value)}>
                <option value="all">All Modules</option>
                {modules.map(module => (
                  <option key={module.moduleId} value={module.moduleId}>
                    {module.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="filter-item">
              <label>Status</label>
              <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                <option value="all">All Status</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending Review</option>
                <option value="needs_revision">Needs Revision</option>
              </select>
            </div>
            
            <div className="filter-item">
              <label>Decision</label>
              <select value={selectedDecision} onChange={(e) => setSelectedDecision(e.target.value)}>
                <option value="all">All Decisions</option>
                <option value="C">Completed</option>
                <option value="R">Repeat</option>
                <option value="Re">Remedial</option>
                <option value="none">No Decision</option>
              </select>
            </div>

            <div className="filter-item">
              <label>Verified By</label>
              <select value={selectedVerifiedBy} onChange={(e) => setSelectedVerifiedBy(e.target.value)}>
                <option value="all">All</option>
                <option value="me">Verified by Me</option>
                <option value="others">Verified by Others</option>
                <option value="unverified">Not Verified</option>
              </select>
            </div>

            <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
          </div>

          <button className="bulk-verify-btn" onClick={handleBulkVerify}>
            <FaCheckDouble /> Bulk Verify
          </button>
        </div>
      </div>

      <div className="logbooks-content" ref={contentRef}>
        {filteredLogbooks.length === 0 ? (
          <div className="no-logbooks">
            <FaBookMedical className="no-logbooks-icon" />
            <h3>No logbook entries found</h3>
            <p>Try adjusting your filters or check back later for new submissions</p>
          </div>
        ) : (
          <div className="logbooks-list">
            {groupLogbooksByMonth().map(([monthKey, monthData]) => (
              <div key={monthKey} className="month-group">
                <div 
                  className="month-header"
                  onClick={() => toggleMonth(monthKey)}
                >
                  <div className="month-title">
                    <span className="month-icon">
                      {expandedMonths[monthKey] ? <FaChevronDown /> : <FaChevronRight />}
                    </span>
                    <FaCalendarAlt />
                    <h3>{monthData.name}</h3>
                    <span className="month-count">{monthData.entries.length} entries</span>
                  </div>
                </div>
                
                {expandedMonths[monthKey] !== false && (() => {
                  const currentPage = monthPages[monthKey] || 1;
                  const totalPages = Math.ceil(monthData.entries.length / ITEMS_PER_PAGE);
                  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
                  const pageEntries = monthData.entries.slice(startIdx, startIdx + ITEMS_PER_PAGE);

                  return (
                  <div className="month-entries">
                    {pageEntries.map(logbook => {
                      const status = getStatusBadge(logbook.status);
                      const isExpanded = expandedCards[logbook._id] !== false;
                      const rating = logbook.review?.rating ? getRatingBadge(logbook.review.rating) : null;
                      const studentBatch = studentBatchMap[String(logbook.studentId?._id || logbook.studentId)];

                      return (
                        <div key={logbook._id} className={`logbook-card ${isExpanded ? 'expanded' : ''}`}>
                          <div className="logbook-header">
                            <div className="student-info">
                              <div className="student-avatar">
                                <FaUser />
                              </div>
                              <div className="student-details">
                                <h4>
                                  {logbook.studentId?.profile?.firstName} {logbook.studentId?.profile?.lastName}
                                  {studentBatch && (
                                    <span className="student-batch-tag">
                                      {batchLabels[studentBatch] || studentBatch}
                                    </span>
                                  )}
                                </h4>
                                <span className="student-email">{logbook.studentId?.email}</span>
                              </div>
                            </div>
                            <div className="status-badges">
                              <div className={`status-badge ${status.class}`}>
                                {status.icon} {status.text}
                              </div>
                              {rating && (
                                <div className={`rating-badge ${rating.class}`}>
                                  <FaStar /> {rating.icon}
                                </div>
                              )}
                              {logbook.reviewHistory?.some(h => !h.reason || h.reason === 'reattempt' || h.reason === 'hod_reattempt') && (
                                <div className="status-badge status-reattempted">
                                  <FaHistory /> Reattempted
                                </div>
                              )}
                              {logbook.reviewHistory?.some(h => h.reason === 'reverified') && (
                                <div className="status-badge status-multi-reviewer">
                                  <FaUserFriends /> Multiple Reviewers
                                </div>
                              )}
                              {logbook.reattemptRequest && (
                                <div className="status-badge status-hod-reattempt">
                                  <FaRedo /> Reattempt Requested
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="logbook-body">
                            <div className="logbook-info">
                              <div className="info-item">
                                <span className="info-label">Module:</span>
                                <span className="info-value">{getModuleName(logbook.moduleId)}</span>
                              </div>
                              <div className="info-item">
                                <span className="info-label">Type:</span>
                                <span className="info-value">{logbook.entryType}</span>
                              </div>
                              <div className="info-item">
                                <span className="info-label">Submitted:</span>
                                <span className="info-value">
                                  {formatDate(logbook.createdAt)}
                                </span>
                              </div>
                              {logbook.review?.reviewerName && (
                                <div className="info-item">
                                  <span className="info-label">Verified by:</span>
                                  <span className="info-value">{logbook.review.reviewerName}</span>
                                </div>
                              )}
                            </div>

                            {(() => {
                              const attCount = logbook.entryType === 'single'
                                ? (logbook.entries?.reduce((sum, e) => sum + (e.attachments?.length || 0), 0) || 0)
                                : (logbook.attachments?.length || 0);
                              if (attCount === 0) return null;
                              return (
                                <div className="attachment-indicator">
                                  <FaPaperclip /> {attCount} item{attCount > 1 ? 's' : ''} attached &middot; click <strong>Full View</strong> to see
                                </div>
                              );
                            })()}

                            {/* Expandable Entry Data Section */}
                            {isExpanded && (
                              <div className="expanded-content">
                                {renderEntryData(logbook, modules)}
                                
                                {logbook.studentNotes && (
                                  <div className="student-notes-preview">
                                    <h5>Student Notes</h5>
                                    <p>{logbook.studentNotes}</p>
                                  </div>
                                )}
                                
                                {logbook.review?.feedback && (
                                  <div className="feedback-preview">
                                    <h5>Verification Remarks</h5>
                                    <p>{logbook.review.feedback}</p>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            <div className="logbook-actions">
                              <button 
                                className="action-btn expand-btn"
                                onClick={() => toggleCard(logbook._id)}
                              >
                                {isExpanded ? (
                                  <>
                                    <FaChevronDown /> Hide Details
                                  </>
                                ) : (
                                  <>
                                    <FaChevronRight /> Show Details
                                  </>
                                )}
                              </button>
                              
                              <button 
                                className="action-btn view-btn"
                                onClick={() => handleViewDetails(logbook)}
                              >
                                <FaEye /> Full View
                              </button>
                              
                              {(() => {
                                const { hasReview, isSameReviewer } = getReviewerMatch(logbook);
                                if (hasReview && isSameReviewer) {
                                  return (
                                    <button
                                      className="action-btn edit-btn"
                                      onClick={() => handleVerificationClick(logbook)}
                                    >
                                      <FaEdit /> Edit Verification
                                    </button>
                                  );
                                }
                                if (hasReview && !isSameReviewer) {
                                  if (!user.profile?.isHOD) {
                                    return null;
                                  }
                                  return (
                                    <button
                                      className="action-btn reverify-btn"
                                      onClick={() => handleVerificationClick(logbook)}
                                    >
                                      <FaUserFriends /> Re-verify
                                    </button>
                                  );
                                }
                                return (
                                  <button
                                    className="action-btn verify-btn"
                                    onClick={() => handleVerificationClick(logbook)}
                                  >
                                    <FaCheckCircle /> Verify
                                  </button>
                                );
                              })()}

                              {user.profile?.isHOD && ['submitted', 'under_review', 'approved'].includes(logbook.status) && (
                                <button
                                  className="action-btn reattempt-request-btn"
                                  onClick={() => handleReattemptClick(logbook)}
                                >
                                  <FaRedo /> Re-attempt
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {totalPages > 1 && (
                      <div className="pagination-controls">
                        <button
                          className="page-btn"
                          disabled={currentPage === 1}
                          onClick={() => setMonthPage(monthKey, currentPage - 1)}
                        >
                          <FaChevronLeft /> Prev
                        </button>
                        <span className="page-info">
                          Page {currentPage} of {totalPages}
                          <span className="page-total-count"> ({monthData.entries.length} entries)</span>
                        </span>
                        <button
                          className="page-btn"
                          disabled={currentPage === totalPages}
                          onClick={() => setMonthPage(monthKey, currentPage + 1)}
                        >
                          Next <FaChevronRight />
                        </button>
                      </div>
                    )}
                  </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Single Verification Modal */}
      {showVerificationModal && (
        <div className="modal-overlay" onClick={closeAllModals}>
          <div className="verification-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-sticky">
              <h3>{isEditing ? 'Edit Verification' : isReverify ? 'Re-verify Logbook' : 'Verify Logbook'}</h3>
              <button 
                className="modal-close"
                onClick={closeAllModals}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="student-info-modal">
                <div className="info-row">
                  <span>Student:</span>
                  <strong>
                    {selectedLogbook?.studentId?.profile?.firstName} {selectedLogbook?.studentId?.profile?.lastName}
                  </strong>
                </div>
                <div className="info-row">
                  <span>Module:</span>
                  <strong>{getModuleName(selectedLogbook?.moduleId)}</strong>
                </div>
              </div>

              {selectedLogbook?.reviewHistory?.length > 0 && renderReviewList(
                selectedLogbook.reviewHistory,
                `Previous Attempt${selectedLogbook.reviewHistory.length > 1 ? 's' : ''}`
              )}

              {isReverify && selectedLogbook?.review?.reviewerId && renderReviewList(
                [selectedLogbook.review],
                `Current Verification (by ${selectedLogbook.review.reviewerName})`
              )}

              <div className="verification-form">
                <div className="form-group">
                  <label>Rating (Optional)</label>
                  <select
                    value={verificationData.rating}
                    onChange={(e) => setVerificationData({
                      ...verificationData,
                      rating: e.target.value
                    })}
                  >
                    <option value="">Not specified</option>
                    <option value="E">⭐⭐⭐ Exceeds Expectations (E)</option>
                    <option value="M">⭐⭐ Meets Expectations (M)</option>
                    <option value="B">⭐ Below Expectations (B)</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Attempt Activity (Optional)</label>
                  <select
                    value={verificationData.attemptActivity}
                    onChange={(e) => setVerificationData({
                      ...verificationData,
                      attemptActivity: e.target.value
                    })}
                  >
                    <option value="">Not specified</option>
                    <option value="F">First or Only (F)</option>
                    <option value="R">Repeat (R)</option>
                    <option value="Re">Remedial (Re)</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Decision of Faculty (Optional)</label>
                  <select
                    value={verificationData.facultyDecision}
                    onChange={(e) => setVerificationData({
                      ...verificationData,
                      facultyDecision: e.target.value
                    })}
                  >
                    <option value="">Not specified</option>
                    <option value="C">Completed (C)</option>
                    <option value="R">Repeat (R)</option>
                    <option value="Re">Remedial (Re)</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Remarks (Optional)</label>
                  <textarea
                    value={verificationData.remarks}
                    onChange={(e) => setVerificationData({
                      ...verificationData,
                      remarks: e.target.value
                    })}
                    placeholder="Enter any additional remarks..."
                    rows="3"
                  />
                </div>
              </div>
            </div>
            
            <div className="modal-footer-sticky">
              <button 
                className="btn-cancel"
                onClick={closeAllModals}
              >
                Cancel
              </button>
              <button
                className={`btn-submit ${isReverify ? 'btn-reverify' : ''}`}
                onClick={handleVerificationSubmit}
                disabled={savingVerification}
              >
                {savingVerification ? (
                  <>
                    <FaSpinner className="spinner" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave />
                    {isEditing ? 'Update Verification' : isReverify ? 'Re-verify' : 'Verify Logbook'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HOD Reattempt Request Modal */}
      {showReattemptModal && (
        <ReattemptRequestModal
          entry={selectedLogbook}
          moduleName={getModuleName(selectedLogbook?.moduleId)}
          onClose={() => setShowReattemptModal(false)}
          onSuccess={(updated) => {
            setLogbooks(prev => prev.map(lb => lb._id === updated._id ? updated : lb));
            setSuccess('Reattempt requested successfully');
          }}
          onError={(msg) => setError(msg)}
        />
      )}

      {/* Bulk Verification Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={closeAllModals}>
          <div className="bulk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-sticky">
              <h3>Bulk Verify Logbooks</h3>
              <button 
                className="modal-close"
                onClick={closeAllModals}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="bulk-selection-header">
                <button 
                  className="select-all-btn"
                  onClick={selectAllForBulk}
                >
                  Select All Unverified
                </button>
                <span className="selection-count">
                  {selectedForBulk.length} selected
                </span>
              </div>

              <div className="bulk-student-list">
                {filteredLogbooks
                  .filter(l => l.status !== 'approved' && l.status !== 'draft')
                  .map(logbook => (
                    <div 
                      key={logbook._id} 
                      className={`bulk-student-item ${selectedForBulk.includes(logbook._id) ? 'selected' : ''}`}
                      onClick={() => toggleBulkSelection(logbook._id)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedForBulk.includes(logbook._id)}
                        onChange={() => {}}
                      />
                      <div className="bulk-student-info">
                        <strong>
                          {logbook.studentId?.profile?.firstName} {logbook.studentId?.profile?.lastName}
                          {(() => {
                            const b = studentBatchMap[String(logbook.studentId?._id || logbook.studentId)];
                            return b ? <span className="student-batch-tag">{batchLabels[b] || b}</span> : null;
                          })()}
                        </strong>
                        <span>{getModuleName(logbook.moduleId)}</span>
                      </div>
                      <span className={`status-badge ${getStatusBadge(logbook.status).class}`}>
                        {getStatusBadge(logbook.status).text}
                      </span>
                    </div>
                  ))}
              </div>

              <div className="bulk-verification-form">
                <h4>Verification Details (Optional)</h4>
                <p className="form-note">These details will be applied to all selected entries</p>
                
                <div className="form-group">
                  <label>Rating</label>
                  <select
                    value={bulkVerificationData.rating}
                    onChange={(e) => setBulkVerificationData({
                      ...bulkVerificationData,
                      rating: e.target.value
                    })}
                  >
                    <option value="">Not specified</option>
                    <option value="E">⭐⭐⭐ Exceeds Expectations (E)</option>
                    <option value="M">⭐⭐ Meets Expectations (M)</option>
                    <option value="B">⭐ Below Expectations (B)</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Attempt Activity</label>
                  <select
                    value={bulkVerificationData.attemptActivity}
                    onChange={(e) => setBulkVerificationData({
                      ...bulkVerificationData,
                      attemptActivity: e.target.value
                    })}
                  >
                    <option value="">Not specified</option>
                    <option value="F">First or Only (F)</option>
                    <option value="R">Repeat (R)</option>
                    <option value="Re">Remedial (Re)</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Decision of Faculty</label>
                  <select
                    value={bulkVerificationData.facultyDecision}
                    onChange={(e) => setBulkVerificationData({
                      ...bulkVerificationData,
                      facultyDecision: e.target.value
                    })}
                  >
                    <option value="">Not specified</option>
                    <option value="C">Completed (C)</option>
                    <option value="R">Repeat (R)</option>
                    <option value="Re">Remedial (Re)</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Remarks</label>
                  <textarea
                    value={bulkVerificationData.remarks}
                    onChange={(e) => setBulkVerificationData({
                      ...bulkVerificationData,
                      remarks: e.target.value
                    })}
                    placeholder="Enter remarks for all selected entries..."
                    rows="3"
                  />
                </div>
              </div>
            </div>
            
            <div className="modal-footer-sticky">
              <button 
                className="btn-cancel"
                onClick={closeAllModals}
              >
                Cancel
              </button>
              <button 
                className="btn-submit"
                onClick={handleBulkVerificationSubmit}
                disabled={savingBulk || selectedForBulk.length === 0}
              >
                {savingBulk ? (
                  <>
                    <FaSpinner className="spinner" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <FaCheckDouble />
                    Verify {selectedForBulk.length} Entries
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && (
        <EntryDetailsModal
          entryId={detailsEntryId}
          onClose={closeAllModals}
          onVerifyClick={(entry) => handleVerificationClick(entry)}
        />
      )}
    </div>
  );
};

export default ViewLogbooks;